import { useEffect, useState } from 'react';
import type { DashboardEquipmentSession } from 'shared';
import { formatRemaining, isOverdue } from '@/lib/sessionTime';

interface LiveEquipmentProps {
  sessions: DashboardEquipmentSession[];
  /** Opens the list where a session can be ended. */
  onManage: () => void;
}

const SHOWN = 8;

function since(startedAt: string, now: number): string {
  const secs = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${secs % 60}s`;
  return `${secs}s`;
}

/** Who is using what, right now. */
export default function LiveEquipment({ sessions, onManage }: LiveEquipmentProps) {
  const [now, setNow] = useState(() => Date.now());

  // The durations run by themselves between two readings of the API.
  useEffect(() => {
    if (sessions.length === 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [sessions.length]);

  return (
    <section className="card" aria-labelledby="live-equipment">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
        <h2 id="live-equipment" className="text-sm font-semibold text-foreground">
          Equipamento em uso agora
          <span className="ml-2 text-xs font-medium text-muted-foreground tabular-nums">{sessions.length}</span>
        </h2>
        {sessions.length > 0 && (
          <button type="button" onClick={onManage} className="text-xs font-medium text-primary hover:underline">
            Gerir sessões
          </button>
        )}
      </div>

      {sessions.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-muted-foreground">Nenhum equipamento em uso.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Equipamento em uso agora</caption>
            <thead>
              <tr className="text-xs font-medium text-muted-foreground bg-muted/30">
                <th scope="col" className="text-left font-medium px-5 py-2">Participante</th>
                <th scope="col" className="text-left font-medium px-3 py-2">Equipamento</th>
                <th scope="col" className="text-right font-medium px-3 py-2">Início</th>
                <th scope="col" className="text-right font-medium px-3 py-2">Há</th>
                <th scope="col" className="text-right font-medium px-5 py-2">Tempo previsto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sessions.slice(0, SHOWN).map((s) => (
                <tr key={s.id}>
                  <td className="px-5 py-2.5 font-medium text-foreground">{s.participantName}</td>
                  <td className="px-3 py-2.5 text-foreground">{s.equipmentName}</td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground tabular-nums">
                    {new Date(s.startedAt).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-3 py-2.5 text-right text-foreground tabular-nums">{since(s.startedAt, now)}</td>
                  <td
                    className={`px-5 py-2.5 text-right tabular-nums ${
                      isOverdue(s.expiresAt ? new Date(s.expiresAt) : null, now)
                        ? 'text-destructive font-medium'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {formatRemaining(s.expiresAt ? new Date(s.expiresAt) : null, now)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sessions.length > SHOWN && (
            <p className="px-5 py-2.5 border-t border-border text-xs text-muted-foreground">
              E mais {sessions.length - SHOWN}.{' '}
              <button type="button" onClick={onManage} className="font-medium text-primary hover:underline">
                Ver todas
              </button>
            </p>
          )}
        </div>
      )}
    </section>
  );
}
